import React from 'react';
import { useNavigate } from 'react-router-dom';
import './DesignEngineeringPage.css';

const DesignEngineeringPage = () => {
  const navigate = useNavigate();

  const services = [
    {
      title: 'CAD Designing',
      icon: '📐',
      description: 'Precision computer-aided design solutions for complex engineering projects.',
      features: [
        '2D and 3D CAD drafting',
        'AutoCAD and SolidWorks expertise',
        'Parametric design modeling',
        'Assembly design and documentation',
        'Bill of materials (BOM) generation',
        'Design revision management'
      ]
    },
    {
      title: '3D Modeling',
      icon: '🎨',
      description: 'Create realistic 3D models for visualization, prototyping, and production.',
      features: [
        'Photorealistic 3D rendering',
        'Surface and solid modeling',
        'Animation and walkthroughs',
        'Virtual prototyping',
        'Product visualization',
        'CAD to 3D model conversion'
      ]
    },
    {
      title: 'Technical Drawings',
      icon: '📋',
      description: 'Detailed technical documentation that meets industry standards.',
      features: [
        'Engineering drawings (ISO/ANSI standards)',
        'Schematic and layout diagrams',
        'Assembly and exploded views',
        'Dimensioning and tolerancing (GD&T)',
        'Manufacturing drawings',
        'As-built documentation'
      ]
    },
    {
      title: 'Product Design',
      icon: '💡',
      description: 'End-to-end product design from concept to manufacturing-ready files.',
      features: [
        'Concept development and sketching',
        'Design for Manufacturing (DFM)',
        'Ergonomic analysis',
        'Material selection guidance',
        'Prototype design support',
        'Design optimization'
      ]
    }
  ];

  const software = [
    { name: 'AutoCAD', category: 'CAD' },
    { name: 'SolidWorks', category: 'CAD' },
    { name: 'CATIA', category: 'CAD' },
    { name: 'Fusion 360', category: '3D Modeling' },
    { name: 'Blender', category: '3D Modeling' },
    { name: 'Rhino', category: '3D Modeling' },
    { name: 'Revit', category: 'BIM' },
    { name: 'SketchUp', category: 'Design' },
    { name: 'Inventor', category: 'CAD' },
    { name: 'KeyShot', category: 'Rendering' }
  ];

  const benefits = [
    {
      title: 'Industry Expertise',
      description: 'Deep knowledge across multiple engineering disciplines',
      icon: '🎓'
    },
    {
      title: 'Precision & Quality',
      description: 'Accuracy you can trust for critical projects',
      icon: '🎯'
    },
    {
      title: 'Fast Turnaround',
      description: 'Quick delivery without compromising quality',
      icon: '⚡'
    },
    {
      title: 'Revision Support',
      description: 'Unlimited revisions until you\'re satisfied',
      icon: '🔄'
    }
  ];

  const industries = [
    {
      name: 'Manufacturing',
      description: 'Machine parts, tooling, and production equipment',
      icon: '⚙️'
    },
    {
      name: 'Architecture',
      description: 'Building design, structural elements, and MEP systems',
      icon: '🏗️'
    },
    {
      name: 'Product Development',
      description: 'Consumer products, packaging, and prototypes',
      icon: '📦'
    },
    {
      name: 'Automotive',
      description: 'Vehicle components, custom parts, and accessories',
      icon: '🚗'
    },
    {
      name: 'Aerospace',
      description: 'Precision components and technical assemblies',
      icon: '✈️'
    },
    {
      name: 'Medical Devices',
      description: 'Healthcare equipment and surgical instruments',
      icon: '🏥'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Consultation',
      description: 'Understanding your project requirements, specifications, and constraints'
    },
    {
      step: '02',
      title: 'Concept Design',
      description: 'Initial sketches and design concepts for your review'
    },
    {
      step: '03',
      title: 'Detailed Design',
      description: 'Complete CAD models with precise dimensions and specifications'
    },
    {
      step: '04',
      title: 'Review & Revisions',
      description: 'Collaborative refinement based on your feedback'
    },
    {
      step: '05',
      title: 'Final Delivery',
      description: 'Production-ready files in your preferred format'
    }
  ];

  const deliverables = [
    'Native CAD files (DWG, SLDPRT, IPT)',
    'Universal formats (STEP, IGES, STL)',
    'PDF technical drawings',
    '3D rendered images',
    'Bill of Materials (BOM)',
    'Design documentation'
  ];

  return (
    <div className="design-engineering-page">
      {/* Hero Section */}
      <section className="design-hero">
        <div className="container">
          <button className="back-btn" onClick={() => navigate('/')}>
            ← Back to Home
          </button>
          <div className="hero-content">
            <h1 className="hero-title">
              Design & Engineering Services
            </h1>
            <p className="hero-subtitle">
              Professional CAD design and engineering solutions that bring your ideas to life with precision and creativity
            </p>
            <div className="hero-stats">
              <div className="stat">
                <h3>200+</h3>
                <p>Designs Completed</p>
              </div>
              <div className="stat">
                <h3>15+</h3>
                <p>Industries Served</p>
              </div>
              <div className="stat">
                <h3>100%</h3>
                <p>Accuracy Guaranteed</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="services-detail">
        <div className="container">
          <h2 className="section-title">Our Design Services</h2>
          <p className="section-subtitle">
            Comprehensive engineering design solutions tailored to your needs
          </p>
          <div className="services-grid">
            {services.map((service, index) => (
              <div key={index} className="service-detail-card">
                <div className="service-icon-large">{service.icon}</div>
                <h3>{service.title}</h3>
                <p className="service-desc">{service.description}</p>
                <ul className="features-list">
                  {service.features.map((feature, idx) => (
                    <li key={idx}>
                      <span className="checkmark">✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Software Tools */}
      <section className="software-tools">
        <div className="container">
          <h2 className="section-title">Software We Use</h2>
          <p className="section-subtitle">
            Industry-standard tools for professional results
          </p>
          <div className="software-grid">
            {software.map((tool, index) => (
              <div key={index} className="software-item">
                <span className="software-name">{tool.name}</span>
                <span className="software-category">{tool.category}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="industries">
        <div className="container">
          <h2 className="section-title">Industries We Serve</h2>
          <p className="section-subtitle">
            Bringing engineering excellence to diverse sectors
          </p>
          <div className="industries-grid">
            {industries.map((industry, index) => (
              <div key={index} className="industry-card">
                <div className="industry-icon">{industry.icon}</div>
                <h3>{industry.name}</h3>
                <p>{industry.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits">
        <div className="container">
          <h2 className="section-title">Why Choose Us</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={index} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="process">
        <div className="container">
          <h2 className="section-title">Our Design Process</h2>
          <p className="section-subtitle">
            A systematic approach to deliver exceptional results
          </p>
          <div className="process-timeline">
            {process.map((item, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{item.step}</div>
                <div className="step-content">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="deliverables">
        <div className="container">
          <h2 className="section-title">What You'll Receive</h2>
          <p className="section-subtitle">
            Complete design packages ready for production
          </p>
          <div className="deliverables-grid">
            {deliverables.map((item, index) => (
              <div key={index} className="deliverable-item">
                <span className="check-icon">✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Bring Your Design to Life?</h2>
            <p>Let's discuss your project requirements and create something amazing together</p>
            <div className="cta-buttons">
              <button className="primary-btn" onClick={() => navigate('/#contact')}>
                Get a Quote
              </button>
              <button className="secondary-btn" onClick={() => navigate('/')}>
                Explore More Services
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignEngineeringPage;
